import { useEffect, useState } from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { message } from "antd";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setUserInfo } from "../../store/features/UserInfo";
import { resumeGoogleSignInIfRequested } from "../../utils/cognitoOAuth";

// Call on mount from LoginPage/RegisterPage. Returns true while the page is
// mid-bounce back from Cognito's /logout and about to continue on to the
// real Google redirect (see cognitoOAuth.js) - callers should show a loading
// state instead of the normal form for that brief moment.
export function useResumingGoogleSignIn() {
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    if (resumeGoogleSignInIfRequested()) {
      setResuming(true);
    }
  }, []);

  return resuming;
}

export const GET_USER_PROFILE = gql`
  query getUserProfile($id: String!) {
    getUserProfile(_id: $id) {
      _id
      about
      name
      profile_image
      roles
      socialLink
      subscriberCount
      lastLoginAt
      cognito_user_status
    }
  }
`;

export const GET_COACH = gql`
  query filterCoaches($filters: FilterCoachesInput) {
    filterCoaches(filters: $filters) {
      count
      limit
      offset
      coaches {
        _id
        name
        email
        belief
        about
        experience
        profile_image
        isFollowed
        socialLink
        expertise {
          _id
          backgroundUrl
          backgroundColor
          linearColor
          description
          title
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_COACH_SUBSCRIPTION_STATUS = gql`
  query getCoachSubscriptionStatus {
    getCoachSubscriptionStatus {
      status
      hasActiveSubscription
      trialEndDate
      currentPeriodStart
      currentPeriodEnd
      cancelAtPeriodEnd
      planType
    }
  }
`;

export const BECOME_COACH_MUTATION = gql`
  mutation becomeCoach($input: BecomeCoachInput!) {
    becomeCoach(input: $input) {
      _id
    }
  }
`;

export const GET_CURRENT_USER_ID = gql`
  query getCurrentUser {
    getCurrentUser {
      _id
    }
  }
`;

// Shared post-login sequence: fetch/provision the coach profile, resolve
// subscription status, and route to the right next screen. Used by both the
// password-login form and the Cognito Hosted UI (Google) callback.
export function useCompleteLogin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [getUserProfile] = useLazyQuery(GET_USER_PROFILE);
  const [getCoach] = useLazyQuery(GET_COACH);
  const [becomeCoach] = useMutation(BECOME_COACH_MUTATION);
  const [getCoachSubscriptionStatus] = useLazyQuery(
    GET_COACH_SUBSCRIPTION_STATUS
  );

  return async function handleAfterLogin(userId) {
    const userData = await getUserProfile({
      variables: { id: userId },
    });

    if (!userData?.data?.getUserProfile?.roles?.includes("COACH")) {
      await becomeCoach({
        variables: {
          input: {
            about: userData.data.getUserProfile?.about,
            belief: userData.data.getUserProfile?.belief,
            experience: userData.data.getUserProfile?.experience,
            socialLink: userData.data.getUserProfile?.socialLink,
          },
        },
      });
    }

    const coachData = await getCoach({
      variables: { filters: { userId } },
    });
    const lastLoginAt = userData.data.getUserProfile.lastLoginAt;
    // Google/Apple accounts are passwordless - never send them to the
    // password-reset screen, even on their first-ever login (when
    // lastLoginAt is still unset, same as a brand-new admin-created
    // email/password account would look).
    const isExternalProvider =
      userData.data.getUserProfile.cognito_user_status === "EXTERNAL_PROVIDER";

    let coach = {};
    if (coachData?.data?.filterCoaches?.coaches?.length > 0) {
      const coachInfo = coachData.data.filterCoaches.coaches[0];
      coach = {
        ...coachInfo,
        expertise: coachInfo.expertise.map((v) => v._id),
      };
    }

    const subscriptionData = await getCoachSubscriptionStatus();
    const subscriptionStatus =
      subscriptionData?.data?.getCoachSubscriptionStatus || null;

    const userInfo = {
      user: userData.data.getUserProfile,
      coach,
      subscriptionStatus,
    };
    dispatch(setUserInfo(userInfo));

    if (!isExternalProvider && (!lastLoginAt || lastLoginAt === null)) {
      navigate("/ResetPwd?type=change");
    } else {
      message.success("Login successfully");
      navigate("/");
    }
  };
}
